import { deleteMatchApi, useMatch } from "@/api/services/admin/match";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Tag } from "@/components/elements/Tag";
import { TopPage } from "@/components/elements/TopPage";
import { MatchStatusTag } from "@/features/match/components/MatchStatusTag";
import { black } from "@/theme/colors";
import { MatchSummary } from "@/features/match/components/MatchSummary";
import { Stack, styled, Typography } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { EMatchContractStatus, USER_TYPE } from "@/types/enum";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { MatchDescription } from "@/features/match/components/MatchDescription";
import { useParams, useNavigate } from "react-router-dom";
import { MatchFooterContainer } from "@/features/match/components/MatchFooterContainer";
import { MatchUserTable } from "@/features/match/components/MatchUserTable";
import { isNumber } from "@/utils/number";
import { useMatchListSessionStorage } from "@/hooks/useMatchListSessionStorage";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";

export const MatchDetailPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getBackUrl } = useMatchListSessionStorage();
  const { data: matchResult, isLoading, isError } = useMatch();
  const matchData = matchResult?.data;
  const matchUsers = matchData?.users ? matchData.users.filter((user) => user.type == USER_TYPE.MATCH_USER) : [];
  const confirmDelete = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();

  const backUrl = useMemo(() => getBackUrl(), [getBackUrl]);

  const isDeleted = !!matchData?.deletedAt;
  const isDeletable =
    !!matchData &&
    !isDeleted &&
    ([EMatchContractStatus.TRIAL_CANCELLED, EMatchContractStatus.CANCELED_CONTRACT] as string[]).includes(matchData.contractStatus);

  const handleDelete = async () => {
    try {
      if (!matchData?.id) return;
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteMatchApi(matchData.id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.FETCH_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.GET_CONTRACT_STATUS_COUNT] });
      navigate(backUrl);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(backUrl, { replace: true });
    }
  }, [id, navigate, backUrl]);

  useEffect(() => {
    if (id && isNumber(id) && !isLoading && (isError || !matchData)) {
      navigate(backUrl, { replace: true });
    }
  }, [id, isLoading, isError, matchData, navigate, backUrl]);

  if (isLoading || !matchData) return <LoadingPage />;

  const getLastLoginTime = () => {
    const timestamps = matchUsers.map((u) => u.lastLoginTime).filter((value): value is string => Boolean(value));
    if (timestamps.length === 0) return "----";
    const latest = Math.max(...timestamps.map((value) => dayjs(value).valueOf()));
    return dayjs(latest).format(DATE_FORMAT);
  };

  return (
    <MatchEditWrapper gap={3}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="flex-end" gap={2}>
            {matchData.matchName}
            <MatchStatusTag status={matchData.contractStatus} variant="normal" />
            {isDeletable && (
              <Button variant="danger" size="sm" onClick={handleDelete}>
                削除
              </Button>
            )}
            {isDeleted && <Tag variant="normal" color={black[95]} title="削除済み" />}
          </Typography>
        }
        description={
          <Typography variant="body14">
            紹介事業許可番号：{matchData.matchLicenseNumber ?? "----"}
            <span style={{ marginLeft: "16px" }}>最終ログイン日：{getLastLoginTime()}</span>
          </Typography>
        }
        enablePageBack={false}
      />
      <MenuNavigate
        contents={[
          { title: "基本情報", content: <MatchSummary /> },
          { title: "その他情報", content: <MatchDescription /> },
          { title: "顧客ユーザー・irohana担当者の一覧", content: <MatchUserTable matchName={matchData.matchName} disabled={isDeleted} /> },
        ]}
        contentWidth="900px"
        contentAffix={<MatchFooterContainer matchId={id} disabled={isDeleted} />}
      />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message={`削除の確認\n削除してよろしいですか？`}
      />
    </MatchEditWrapper>
  );
};

const MatchEditWrapper = styled(Stack)`
  margin: 0 auto;
`;
